/** @jsxImportSource ../../i18n */
import React, { useRef } from 'react';
import { Check } from 'lucide-react';
import { cx } from '../../theme/cx';
import { IconTile } from './Card';

const STEPS = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

// Radio-group keyboard model for custom-looking options: one tab stop (the
// checked option, else the first), arrows move and select, Home/End jump.
// Returns a function giving the props of each option button.
export const useRadioGroup = ({ options, value, onChange, getValue = (option) => option.value }) => {
  const refs = useRef([]);
  const checkedIndex = options.findIndex((option) => getValue(option) === value);
  const stopIndex = checkedIndex === -1 ? 0 : checkedIndex;
  const select = (index) => {
    refs.current[index]?.focus();
    onChange(getValue(options[index]));
  };
  return (option, index) => ({
    ref: (el) => { refs.current[index] = el; },
    type: 'button',
    role: 'radio',
    'aria-checked': index === checkedIndex,
    tabIndex: index === stopIndex ? 0 : -1,
    onClick: () => onChange(getValue(option)),
    onKeyDown: (event) => {
      let next = null;
      if (event.key in STEPS) next = (index + STEPS[event.key] + options.length) % options.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = options.length - 1;
      if (next === null) return;
      event.preventDefault();
      select(next);
    },
  });
};

export const RadioMark = ({ checked, className }) => (
  <span aria-hidden="true" className={cx('inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border transition-colors duration-150', checked ? 'border-gold-500 bg-gold-500 text-on-gold' : 'border-line-strong', className)}>
    {checked && <Check size={12} strokeWidth={3} />}
  </span>
);

// Selectable cards behaving as one radio group (icon, label, description).
export const RadioCards = ({ options, value, onChange, ariaLabel, className }) => {
  const itemProps = useRadioGroup({ options, value, onChange });
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cx('grid gap-3', className)}>
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            {...itemProps(option, index)}
            className={cx(
              'flex w-full items-start gap-3 rounded-control border p-4 text-left transition-colors duration-150',
              checked ? 'border-gold-500/50 bg-gold-950/40 shadow-[inset_0_0_0_1px_rgb(var(--c-gold-500)/0.2)]' : 'border-line bg-surface-2/40 hover:border-line-strong hover:bg-surface-2',
            )}
          >
            {option.icon && <IconTile icon={option.icon} size="sm" tone={checked ? 'gold' : 'neutral'} />}
            <span className="min-w-0 flex-1">
              <span className="block font-medium text-fg">{option.label}</span>
              {option.description && <span className="mt-0.5 block text-sm leading-relaxed text-fg-muted">{option.description}</span>}
            </span>
            <RadioMark checked={checked} className="mt-0.5" />
          </button>
        );
      })}
    </div>
  );
};
